/**
 * Meta Ads: connect the ad account, pick which one.
 *
 * The server is the source of truth for whether a connection exists; this
 * screen only asks and draws. Disconnecting is destructive enough to confirm
 * first - the live page used window.confirm, this uses the app's own sheet.
 *
 * @module web/screens/app/MetaScreen
 */
import { useEffect, useState } from "react";
import { get, post } from "../../api/client.ts";
import { formatSpend, formatCount, panelState, withoutAd, canSubmit, type UnmatchedAd } from "./unmatchedAds.ts";
import { metaPanelView, accountLabel, PING_STEP, PINGED_TITLE, TELL_US_LABEL, pingedOnLabel, META_SETUP_VIDEO_URL } from "./metaPanel.ts";
import type { MetaStatusResponse } from "../../api/types.ts";
import { useBrand } from "../../state/BrandContext.tsx";

interface MetaAccount {
  id: string;
  name?: string;
}


/**
 * The creative, or something designed to stand in for it.
 *
 * Meta's thumbnail link expires, and until the server started refreshing it
 * this row drew the browser's torn-page glyph — 57 of them in a column, on the
 * one panel whose entire premise is that the picture is what the client
 * recognises. Two guards, because a signed URL can still die between the
 * server reading it and the browser asking for it:
 *
 *   - `onError` swaps in the tile rather than leaving the broken glyph;
 *   - the tile carries the ad's first letter, so the rows stay distinguishable
 *     even when every picture is gone.
 *
 * `loading="lazy"` because a brand can have hundreds of these and only a
 * handful are ever on screen.
 */
function AdThumb({ url, name }: { url: string | null; name: string }) {
  const [failed, setFailed] = useState(false);
  const box: React.CSSProperties = {
    width: 64, height: 64, borderRadius: 8, flexShrink: 0,
  };
  if (!url || failed) {
    return (
      <div
        style={{
          ...box,
          display: "grid", placeItems: "center",
          background: "var(--surface-raised)",
          border: "1px solid var(--glass-border)",
          color: "var(--ink-3)", fontSize: 20, fontWeight: 600,
        }}
        aria-hidden="true"
      >
        {name.trim().charAt(0).toUpperCase() || "?"}
      </div>
    );
  }
  return (
    <img
      src={url}
      alt=""
      loading="lazy"
      width={64}
      height={64}
      onError={() => setFailed(true)}
      style={{ ...box, objectFit: "cover" }}
    />
  );
}

/**
 * "Which one is this?" - the client places their own ads.
 *
 * Not an error panel. Of 755 real ads nothing automatic could place two thirds
 * of them, because the ad name does not say which concept it came from. What
 * does say is the picture, so the picture is the biggest thing in the row.
 */
function UnmatchedAds() {
  const { notify } = useBrand();
  const [ads, setAds] = useState<UnmatchedAd[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pasted, setPasted] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => { void load(); }, []);

  async function load(): Promise<void> {
    setLoading(true);
    try {
      const d = await get<{ ads?: UnmatchedAd[] }>("/api/brand/meta/unmatched");
      setAds(d.ads ?? []);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not read your ads.");
    }
    setLoading(false);
  }

  async function linkCard(adId: string): Promise<void> {
    setBusy(adId);
    try {
      const r = await post<{ conceptName?: string }>(
        `/api/brand/meta/unmatched/${encodeURIComponent(adId)}/link`,
        { cardUrl: pasted[adId] ?? "" },
      );
      // Remove the row rather than reloading the list: a reload would re-fetch
      // every ad to learn one thing, and the client would watch the whole panel
      // blink for each one they place.
      setAds(prev => withoutAd(prev, adId));
      notify(r.conceptName ? `Linked to "${r.conceptName}".` : "Linked.", "success");
    } catch (e) {
      notify(e instanceof Error ? e.message : "Could not link that card.", "error");
    }
    setBusy(null);
  }

  const state = panelState(loading, error, ads);

  return (
    <div className="card fade-in" style={{ marginTop: 24, maxWidth: 720 }}>
      <h3 className="t-h3" style={{ color: "var(--ink-1)" }}>Which one is this?</h3>
      <p className="t-body-sm" style={{ marginTop: 4 }}>
        These ads are spending, and we can&rsquo;t tell which brief they came from.
        Paste the Trello card link and we&rsquo;ll keep them together from now on.
      </p>

      {state.kind === "loading" && (
        <p className="t-body-sm" style={{ marginTop: 16 }}>Loading your ads&hellip;</p>
      )}

      {state.kind === "error" && (
        <p className="status-msg err" style={{ marginTop: 16 }}>{state.message}</p>
      )}

      {state.kind === "empty" && (
        <p className="t-body-sm" style={{ marginTop: 16 }}>
          Every ad that&rsquo;s spending is matched to a brief. Nothing to do here.
        </p>
      )}

      {state.kind === "list" && (
        <>
          <h4 style={{ fontSize: 14, fontWeight: 600, margin: "20px 0 8px" }}>{state.heading}</h4>
          <div className="meta-accounts-list">
            {state.ads.map(a => (
              <div className="meta-account-item" key={a.metaAdId} style={{ alignItems: "flex-start", gap: 12 }}>
                <AdThumb url={a.thumbnailUrl} name={a.adName} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, overflowWrap: "anywhere" }}>{a.adName}</div>
                  <div style={{ fontSize: 12, color: "var(--text-3)" }}>
                    {formatSpend(a.spendCents)} spent &middot; {formatCount(a.impressions)} impressions
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                    {/* Same shape as the board paste field in the checklist
                        (#75): a url input, a real ellipsis rather than an
                        entity, a label a screen reader can use, and Enter to
                        submit - nobody reaches for the mouse after pasting. */}
                    <input
                      type="url"
                      inputMode="url"
                      style={{ flex: 1, minWidth: 180 }}
                      placeholder="https://trello.com/c/…"
                      aria-label={`Trello card link for ${a.adName}`}
                      value={pasted[a.metaAdId] ?? ""}
                      onChange={e => setPasted(p => ({ ...p, [a.metaAdId]: e.target.value }))}
                      onKeyDown={e => {
                        if (e.key === "Enter" && canSubmit(pasted[a.metaAdId] ?? "", busy === a.metaAdId)) {
                          e.preventDefault();
                          void linkCard(a.metaAdId);
                        }
                      }}
                    />
                    <button
                      className="btn btn-secondary"
                      style={{ padding: "8px 16px", fontSize: 13 }}
                      disabled={!canSubmit(pasted[a.metaAdId] ?? "", busy === a.metaAdId)}
                      onClick={() => void linkCard(a.metaAdId)}
                    >
                      {busy === a.metaAdId ? "Linking…" : "Link"}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function MetaScreen() {
  const { notify, refresh } = useBrand();
  const [status, setStatus] = useState<MetaStatusResponse | null>(null);
  const [accounts, setAccounts] = useState<MetaAccount[]>([]);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  /**
   * Set from the SERVER, not from having pressed the button this session.
   *
   * It was local state, so a reload forgot it: the acknowledgement vanished and
   * the button came back live, which made "did that work?" answerable only by
   * pressing again. `pingedAt` from the ping's own response covers the gap
   * between pressing and the next load.
   */
  const [pingedAt, setPingedAt] = useState<string | null>(null);

  useEffect(() => { void load(); }, []);

  async function load(): Promise<void> {
    let current: MetaStatusResponse | null = null;
    try {
      current = await get<MetaStatusResponse>("/api/meta/status");
      setStatus(current);
      setPingedAt(current.sharePingedAt ?? null);
    } catch {
      // Meta not configured, or unreachable. The screen renders "Not
      // Connected", which is the truth from the customer's point of view.
    }

    // Only ask for ad accounts once there is a connection to ask through.
    // GET /api/meta/accounts answers 400 "No Meta connection" when the brand
    // has no access token - which is most brands, most of the time - so calling
    // it unconditionally put a guaranteed 4xx on every visit to this tab. The
    // catch below swallowed it, so the screen looked right and the only trace
    // was in the server log.
    // Only an OAuth brand has a token of its own to enumerate accounts with.
    // A partner-access brand is connected but holds no token, so asking would
    // put a guaranteed 400 "No Meta connection" on every visit - the exact
    // mistake the comment above records, which `connected` alone no longer
    // prevents now that it is true for both paths.
    if (current?.connectionKind !== "oauth") {
      setAccounts([]);
      return;
    }
    try {
      const d = await get<{ accounts?: MetaAccount[] }>("/api/meta/accounts");
      setAccounts(d.accounts ?? []);
    } catch {
      setAccounts([]);
    }
  }

  async function tellUs(): Promise<void> {
    setBusy(true);
    try {
      // The response carries when it was recorded, so the panel switches to the
      // acknowledgement without a second round trip - and shows the same date a
      // reload will.
      const r = await post<{ pingedAt?: string }>("/api/brand/meta/shared");
      setPingedAt(r.pingedAt ?? new Date().toISOString());
      notify("Got it - we'll finish the last step and your numbers will appear.", "success");
    } catch (e) {
      notify(e instanceof Error ? e.message : "Could not reach us just now.", "error");
    }
    setBusy(false);
  }

  async function connect(): Promise<void> {
    try {
      const { url } = await get<{ url?: string }>("/api/meta/connect");
      if (url) { window.location.href = url; return; }
      notify("Meta is not configured yet.", "error");
    } catch (e) {
      notify(e instanceof Error ? e.message : "Could not reach Meta.", "error");
    }
  }

  async function selectAccount(id: string): Promise<void> {
    setBusy(true);
    try {
      await post("/api/meta/select-account", { accountId: id });
      await load();
      notify("Ad account selected.", "success");
    } catch (e) {
      notify(e instanceof Error ? e.message : "Could not select that account.", "error");
    }
    setBusy(false);
  }

  async function disconnect(): Promise<void> {
    setBusy(true);
    try {
      await post("/api/meta/disconnect");
      await load();
      await refresh();
      notify("Meta disconnected.", "success");
    } catch (e) {
      notify(e instanceof Error ? e.message : "Could not disconnect.", "error");
    }
    setBusy(false);
    setConfirming(false);
  }

  // What this tab says now comes from one tested place. It used to read
  // `status.connected`, which is `brands.meta_connected` - "finished Facebook
  // OAuth" - so every partner-access brand was told it was not connected while
  // its ads were listed directly below. See metaPanel.ts.
  // `pingedAt` is threaded in rather than read straight off `status`, so the
  // panel updates the instant the button is pressed instead of waiting for the
  // next status load.
  const view = metaPanelView(status ? { ...status, sharePingedAt: pingedAt } : null);
  const told = view.pinged;
  const pingedOn = pingedOnLabel(pingedAt);

  return (
    <>
      <header className="page-head fade-in">
        <h1 className="t-h2">Meta Ads</h1>
        <p className="t-body" style={{ marginTop: 4 }}>
          Connect your Meta ad account to enable ad reporting &amp; iterations.
        </p>
      </header>

      <div className="card fade-in" style={{ marginTop: 28, maxWidth: 720 }}>
        <div className="meta-status-card">
          <div className={`meta-sicon ${view.pill.tone === "success" ? "on" : "off"}`}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <h3 className="t-h3" style={{ color: "var(--ink-1)" }}>{view.heading}</h3>
            <p className="t-body-sm" style={{ marginTop: 4 }}>{view.detail}</p>
          </div>
          <span className={`pill pill-${view.pill.tone}`}>
            {view.pill.text}
          </span>
        </div>

        {view.showAccount && (
          <div style={{ marginTop: 20, paddingTop: 20, borderTop: "1px solid var(--ink-4)" }}>
            <div className="billing-details" style={{ margin: 0, padding: 0, border: 0 }}>
              <div className="billing-item">
                <label>Account</label>
                {/* adAccountName, not accountName. The live page read
                    `d.accountName || d.accountId` and the route sends neither of
                    those spellings, so this row showed "-" even for a brand with
                    a real ad account linked. The name is now persisted when the
                    account is picked, so this reads as a name; the id stays the
                    fallback for brands connected before that. */}
                <span>{accountLabel(status)}</span>
              </div>
              {/* No "Status: Connected" row. The heading says Connected, the
                  pill beside it says CONNECTED, and this said it a third time
                  on the same card. One statement of a fact is enough. */}
              {status?.adAccountName && status.adAccountId && (
                <div className="billing-item">
                  <label>Account ID</label>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--ink-3)" }}>
                    {status.adAccountId}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {accounts.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Ad Accounts</h4>
            <div className="meta-accounts-list">
              {accounts.map(a => (
                <div className="meta-account-item" key={a.id}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{a.name || a.id}</div>
                    <div style={{ fontSize: 12, color: "var(--text-3)" }}>{a.id}</div>
                  </div>
                  <button
                    className="btn btn-secondary"
                    style={{ padding: "8px 16px", fontSize: 13 }}
                    disabled={busy}
                    onClick={() => void selectAccount(a.id)}
                  >
                    Select
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {view.steps.length > 0 && (
          <div style={{ marginTop: 20, paddingTop: 20, borderTop: "1px solid var(--ink-4)" }}>
            <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>How to share your ad account</h4>
            {/* The button sits INSIDE its own step rather than under the list.
                Below the list it read as "and also, separately, this" - which
                is how a client finishes step 3, sees a button about something
                else, and never presses it.

                Order within the step: sentence, then explanation, then the
                control. The button used to sit between the two sentences, which
                reads as an interruption - you press it mid-thought and only
                afterwards find the line telling you what it does. */}
            <ol style={{ margin: 0, paddingLeft: 20, display: "grid", gap: 6 }}>
              {view.steps.map((step, i) => (
                <li key={i} className="t-body-sm">
                  {step === PING_STEP && told ? PINGED_TITLE : step}
                  {step === PING_STEP && (
                    told ? (
                      /* No button at all once we have the message. A second
                         press cannot do anything useful, and a live control
                         under a heading that still says "Action needed" invites
                         one. Just the date, small, so a long wait is visible. */
                      pingedOn && (
                        <div className="t-mono" style={{ marginTop: 4, fontSize: 11 }}>{pingedOn}</div>
                      )
                    ) : (
                      <div style={{ marginTop: 10 }}>
                        <button className="btn btn-brand" disabled={busy} onClick={() => void tellUs()}>
                          {busy ? "Pinging…" : TELL_US_LABEL}
                        </button>
                      </div>
                    )
                  )}
                </li>
              ))}
            </ol>

            {/* A walkthrough video, directly below the written steps. Rendered only
                when a video url is set (metaPanel.META_SETUP_VIDEO_URL), so an empty
                value ships nothing rather than a blank iframe. */}
            {META_SETUP_VIDEO_URL && (
              <div style={{ marginTop: 16 }}>
                <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Watch: how to set it up</h4>
                <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, borderRadius: 12, overflow: "hidden", border: "1px solid var(--ink-4)" }}>
                  <iframe
                    src={META_SETUP_VIDEO_URL}
                    title="How to share your Meta ad account"
                    frameBorder="0"
                    allow="clipboard-write; fullscreen"
                    allowFullScreen
                    style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {(view.showConnectButton || view.showDisconnect) && (
          <div style={{ marginTop: 24, paddingTop: 24, borderTop: "1px solid var(--ink-4)", display: "flex", gap: 12 }}>
            {view.showConnectButton && (
              <button className="btn btn-brand" onClick={() => void connect()}>
                {view.pill.tone === "success" ? "Reconnect" : "Connect Meta Account"}
              </button>
            )}
            {view.showDisconnect && (
              <button className="btn btn-danger" onClick={() => setConfirming(true)}>Disconnect</button>
            )}
          </div>
        )}
      </div>

      {/* Only when there is an ad account to read ads from.
          Rendered unconditionally, this put a guaranteed 502 on every visit to
          the tab - the warehouse has nothing for a brand whose ads were never
          synced, and asking anyway is an error the client can see in the
          console. Exactly the mistake the /api/meta/accounts comment above
          records, one endpoint over.

          The predicate is `adAccountId`, NOT `connected`. `metaConnected` means
          "finished Facebook OAuth", which a brand on the shared system-user
          token never does - gating on it would hide this panel from precisely
          the brands that have ads (#80). */}
      {status?.adAccountId && <UnmatchedAds />}

      {confirming && (
        <>
          <div className="pay-sheet-overlay open" onClick={() => setConfirming(false)} />
          <div className="pay-sheet open" data-state="ask" role="dialog" aria-modal="true">
            <div className="pay-grip" />
            <div className="pay-body">
              <div className="pay-eyebrow">Disconnect Meta</div>
              <h2 className="pay-title">Stop ad reporting?</h2>
              <button className="pay-cta" disabled={busy} onClick={() => void disconnect()}>
                {busy ? "Disconnecting…" : "Disconnect"}
              </button>
              <button className="pay-cancel" onClick={() => setConfirming(false)}>Cancel</button>
              <p className="pay-note">
                We stop reading your ad performance. You can reconnect at any time.
              </p>
            </div>
          </div>
        </>
      )}
    </>
  );
}
